import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  Grid,
} from '@mui/material';
import * as XLSX from 'xlsx';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DiscrepancyModal from './DiscrepancyModal';
import API from '@Services/apiAxios';
import SbButton from '@Components/Button';
import SbTooltip from '@Components/Tooltip';
import { InfoRounded } from '@mui/icons-material';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@Store';
import { EncryptedPayload } from '../BulkPurchaseInvoice/types';

type Props = {
  carrierId: string;
  onParsedData: (rows: any[]) => void;
  onNext: () => void;
  onBack: () => void;
  setEncryptedPayload: React.Dispatch<SetStateAction<EncryptedPayload>>;
  setShipmentNumbers: React.Dispatch<SetStateAction<string[]>>;
};

type UploadError = {
  message: string;
  type: 'file' | 'parsing' | 'validation' | 'network' | 'unknown';
  details?: string;
};

type FileValidation = {
  isValid: boolean;
  errors: string[];
};

export default function Step2UploadExcel({
  carrierId,
  onParsedData,
  onNext,
  setEncryptedPayload,
  setShipmentNumbers,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [parsedRows, setParsedRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState<UploadError | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileName, setFileName] = useState<string>('');
  const [fileSize, setFileSize] = useState<number>(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateFile = (file: File): FileValidation => {
    const errors: string[] = [];
    const maxSize = 10 * 1024 * 1024; // 10MB

    // Check file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type)) {
      errors.push('Invalid file type. Please upload .xlsx or .xls files only.');
    }

    // Check file size
    if (file.size > maxSize) {
      errors.push('File size exceeds 10MB limit.');
    }

    // Check file name length
    if (file.name.length > 100) {
      errors.push('File name is too long.');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const toNumber = (value: any): number => {
    if (value === null || value === undefined || value === '') return 0;

    if (typeof value === 'string') {
      const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }

    // ✅ Handle numbers with commas (like "8,018.30")
    if (typeof value === 'string') {
      const cleaned = value.replace(/,/g, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }

    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const handleError = (error: UploadError) => {
    setError(error);
    console.error(`${error.type} Error:`, error.message, error.details);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const processFile = async (file: File) => {
    setError(null);
    setUploadProgress(0);
    setFileName(file.name);
    setFileSize(file.size);

    const validation = validateFile(file);
    if (!validation.isValid) {
      handleError({
        message: 'File validation failed',
        type: 'file',
        details: validation.errors.join(', '),
      });
      return;
    }

    setLoading(true);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array', cellDates: true });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // Get raw data with headers to preserve structure
      const rawData: any[][] = XLSX.utils.sheet_to_json(sheet, {
        defval: '',
        raw: false,
        dateNF: 'yyyy-mm-dd',
        header: 1,
      });

      if (rawData.length < 3) {
        throw new Error('Not enough data rows in the Excel file');
      }

      // Extract headers
      const mainHeaders = rawData[0];
      const subHeaders = rawData[1];

      // Combine headers for dimensions
      const combinedHeaders = mainHeaders.map((header, index) => {
        if (header === '' && subHeaders[index]) {
          return subHeaders[index];
        }
        return header;
      });

      // Process data rows (starting from row 3)
      const dataRows = rawData.slice(2);
      const shipments = [];

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const rowData: any = {};

        // Create object with combined headers
        combinedHeaders.forEach((header, index) => {
          if (header && row[index] !== undefined) {
            rowData[header] = row[index];
          }
        });

        // Only process rows with shipment data
        if (rowData['Shipment Number'] || rowData['Carrier AWB No']) {
          // For rows missing dimensions but having volumetric data,
          // look ahead/behind for dimension rows
          if ((!rowData['Length'] || !rowData['Breadth'] || !rowData['Height']) && rowData['Volumetric Weight']) {
            // Check next row for dimensions
            if (i + 1 < dataRows.length) {
              const nextRow = dataRows[i + 1];
              const nextRowData: any = {};
              combinedHeaders.forEach((header, index) => {
                if (header && nextRow[index] !== undefined) {
                  nextRowData[header] = nextRow[index];
                }
              });

              // If next row has dimensions but no shipment data, use those dimensions
              if (
                (nextRowData['Length'] || nextRowData['Breadth'] || nextRowData['Height']) &&
                !nextRowData['Shipment Number'] &&
                !nextRowData['Carrier AWB No']
              ) {
                rowData['Length'] = rowData['Length'] || nextRowData['Length'];
                rowData['Breadth'] = rowData['Breadth'] || nextRowData['Breadth'];
                rowData['Height'] = rowData['Height'] || nextRowData['Height'];
                rowData['Weight'] = rowData['Weight'] || nextRowData['Weight'];
              }
            }
          }

          // Detect charge columns dynamically
          const allKeys = Object.keys(rowData);
          const detectedChargeTypes = allKeys.filter(
            key =>
              ![
                'Sr. No',
                'Shipment Number',
                'Carrier AWB No',
                'Unit',
                'Tax Applicable',
                'No. of Boxes',
                'Actual Weight',
                'Volumetric Weight',
                'Chargeble Weight',
                'Length',
                'Breadth',
                'Height',
                'Weight',
              ].includes(key) &&
              key !== '__EMPTY' &&
              !key.startsWith('__EMPTY'),
          );

          const shipment = {
            shipmentNumber: rowData['Shipment Number']?.toString()?.trim() || '',
            awbNumber: rowData['Carrier AWB No']?.toString()?.trim() || '',
            unit: rowData['Unit']?.toString()?.trim() || 'kg-cm',
            isTaxed:
              rowData['Tax Applicable']?.toString()?.trim()?.toLowerCase() === 'yes' ||
              rowData['Tax Applicable']?.toString()?.trim()?.toLowerCase() === 'true'
                ? true
                : false,
            actualWeight: toNumber(rowData['Actual Weight']),
            chargeableWeight: toNumber(rowData['Chargeble Weight']),
            length: toNumber(rowData['Volumetric Weight']),
            breadth: toNumber(rowData['Breadth']),
            height: toNumber(rowData['Height']),
            weight: toNumber(rowData['Weight']),
            numberOfBoxes: toNumber(rowData['No. of Boxes']),
            charges: detectedChargeTypes
              .map(name => ({
                name,
                amount: toNumber(rowData[name]),
              }))
              .filter(c => c.amount !== 0),
          };

          shipments.push(shipment);
        }
      }

      if (shipments.length === 0) {
        throw new Error('No valid shipment data found in the Excel file');
      }

      const payload = { shipments, carrierId };

      const response = await API.post(
        '/invoice/purchase-invoice/shipment-charges-discrepancy-for-credit-or-debit-note',
        payload,
      );
      setModalData(response.data.data);
      setEncryptedPayload(response.data.data.encryptedPayload);
      setShipmentNumbers(response.data.data.shipmentNumbers);
      setModalOpen(true);
      setParsedRows(shipments);
      onParsedData(shipments);
    } catch (err: any) {
      handleError({
        message: err?.response?.data?.msg || err.message || 'File processing error',
        type: 'file',
        details: err?.response?.data?.errorDetails,
      });
      onParsedData([]);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDownloadTemplate = () => {
    window.open('https://s3.ap-south-1.amazonaws.com/speedbox-static-file-2.0/BulkInvoiceUpdate.xlsx', '_blank');
  };

  useEffect(() => {
    if (error?.message) {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: `Oops! ${error.message}`,
          subTitle: error.details || '',
        }),
      );
    }
  }, [error]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload Excel File
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 3 }}>
        Upload your bulk invoice Excel file for processing. Ensure the file follows the required format.
      </Typography>

      {/* Upload Area */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: 'center',
          mb: 3,
          border: isDragOver ? `2px dashed ${theme.palette.primary.main}` : undefined,
          backgroundColor: isDragOver ? 'action.hover' : undefined,
          transition: 'all 0.2s ease',
          position: 'relative',
          overflow: 'hidden',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: `${uploadProgress}%`,
              height: '100%',
              backgroundColor: 'primary.light',
              opacity: 0.1,
              transition: 'width 0.3s ease',
            }}
          />
        )}

        <UploadIcon color={loading ? 'primary' : 'action'} sx={{ fontSize: 48, mb: 2 }} />

        <Typography variant="h6" gutterBottom>
          {loading ? 'Processing File...' : 'Drag & Drop Your File'}
        </Typography>

        <Typography color="textSecondary" sx={{ mb: 2 }}>
          Supported format: .xlsx, .xls (Max file size: 10MB)
        </Typography>

        {fileName && (
          <Card variant="outlined" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                {loading ? (
                  <CircularProgress size={20} />
                ) : parsedRows.length > 0 ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="primary" />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap title={fileName}>
                    {fileName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {(fileSize / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                {parsedRows.length > 0 && (
                  <Chip label={`${parsedRows.length} shipments`} size="small" color="success" variant="outlined" />
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={loading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            disabled={loading}
            sx={{ minWidth: 140, padding: '10px 6px', borderRadius: '40px', fontSize: '14px' }}
          >
            {loading ? 'Uploading...' : 'Upload File'}
            <input ref={fileInputRef} type="file" hidden accept=".xlsx,.xls" onChange={handleFileChange} />
          </Button>

          <SbButton variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} disabled={loading}>
            Download sample file
          </SbButton>
        </Stack>

        {loading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Processing... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </Paper>

      {/* File Requirements */}
      <Grid container item xs={12} md={'auto'} alignItems="center">
        <Box component="span" className="mr-1" style={{ textDecoration: 'underline' }}>
          View guidelines
        </Box>
        <SbTooltip
          placement="top-end"
          title={
            <>
              1. Required columns: Shipment Number, Carrier AWB No
              <br />
              2. Weight fields should be numeric values <br />
              3. Charge amounts should be in the corresponding charge type columns
            </>
          }
        >
          <InfoRounded />
        </SbTooltip>
      </Grid>

      {/* Discrepancy Modal */}
      <DiscrepancyModal
        open={modalOpen}
        data={modalData}
        onClose={() => setModalOpen(false)}
        onCreateInvoice={() => {
          setModalOpen(false);
          onNext();
        }}
      />
    </Box>
  );
}
